/**
 * v-tutorial Directive
 * Marks elements as guide/tutorial targets
 *
 * Usage:
 * <button v-tutorial="{
 *   id: 'my-tutorial',
 *   text: 'הסבר על הכפתור',
 *   importance: 'info',       // 'info' | 'important' | 'critical'
 *   showOnce: true,
 *   autoTrigger: true,
 *   step: 1,
 *   group: 'intro-group',
 * }">
 *
 * Passing null/undefined skips registration (conditional tutorials).
 */

import { useTutorial } from './useTutorial'

/**
 * Parse directive value; returns null for intentionally-empty values
 */
function parseConfig(value) {
  if (value === null || value === undefined) return null

  if (typeof value === 'string') return { id: value, text: '' }

  if (typeof value !== 'object' || !value.id) {
    console.warn('[v-tutorial] Invalid directive value:', value)
    return null
  }

  return {
    id: value.id,
    text: value.text || '',
    importance: value.importance || 'info',
    showOnce: value.showOnce !== false,
    autoTrigger: value.autoTrigger || false,
    step: value.step || 1,
    group: value.group || null,
    intro: value.intro !== false,
  }
}

function isSameConfig(a, b) {
  if (!a || !b) return false
  return (
    a.id === b.id &&
    a.text === b.text &&
    a.importance === b.importance &&
    a.showOnce === b.showOnce &&
    a.autoTrigger === b.autoTrigger &&
    a.step === b.step &&
    a.group === b.group &&
    a.intro === b.intro
  )
}

/**
 * Visible = participates in layout (display:none elements are skipped)
 */
function isElementVisible(el) {
  return !!(el && (el.offsetParent !== null || el.offsetWidth || el.offsetHeight))
}

export const vTutorial = {
  mounted(el, binding) {
    const config = parseConfig(binding.value)
    if (!config) return

    el._tutorialConfig = config

    // Wait a frame so CSS is applied before the visibility check
    requestAnimationFrame(() => {
      if (!isElementVisible(el) || el._tutorialConfig !== config) return
      useTutorial().registerElement(config.id, el, config)
    })
  },

  updated(el, binding) {
    const newConfig = parseConfig(binding.value)
    const oldConfig = el._tutorialConfig

    // Config removed (e.g. conditional tutorial turned off) -> unregister
    if (!newConfig) {
      if (oldConfig) {
        useTutorial().unregisterElement(oldConfig.id)
        delete el._tutorialConfig
      }
      return
    }

    if (!isElementVisible(el)) return
    if (isSameConfig(oldConfig, newConfig)) return

    const { registerElement, unregisterElement } = useTutorial()
    if (oldConfig && oldConfig.id !== newConfig.id) {
      unregisterElement(oldConfig.id)
    }
    el._tutorialConfig = newConfig
    registerElement(newConfig.id, el, newConfig)
  },

  unmounted(el) {
    const config = el._tutorialConfig
    if (!config) return
    useTutorial().unregisterElement(config.id)
    delete el._tutorialConfig
  },
}

export default vTutorial
